import apiClient from '../apiClient';
import type { ApiResponse } from '@/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ReactQueryKeys } from '../reactQueryKeys';

/**
 * Nested shape mirrors the backend's storage format so partial updates
 * don't drop sibling sub-keys we haven't surfaced in the UI yet.
 */
export interface FeatureSettings {
  cardTopup?: { enabled?: boolean };
  /** "Dopuna gotovinom" (L-01). Unset = ON on the backend. */
  cashTopup?: { enabled?: boolean };
}

const URL = '/settings/features';

export const getFeatureSettings = async (): Promise<FeatureSettings> => {
  const { data } = await apiClient.get<ApiResponse<FeatureSettings>>(URL);
  return data.data;
};

export const updateFeatureSettings = async (
  patch: FeatureSettings,
): Promise<FeatureSettings> => {
  const { data } = await apiClient.put<ApiResponse<FeatureSettings>>(URL, patch);
  return data.data;
};

export const useGetFeatureSettings = () =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_FEATURE_SETTINGS],
    queryFn: getFeatureSettings,
  });

export const useUpdateFeatureSettings = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateFeatureSettings,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ReactQueryKeys.GET_FEATURE_SETTINGS] });
    },
  });
};
